/**
 * Solanaland
 *
 * Author: Venus Lumanglas
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import React from "react";
import { Col, Container, Row, Spinner } from "react-bootstrap";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import { Card, Button, Form } from "core/components";
import { PerformanceRateFormData, PerformanceRateItemRatingType, PerformanceRateSchema } from "core/schema";
import PerformanceRateItemForm from "./PerformanceRateItemForm";
import { yupResolver } from "@hookform/resolvers/yup";
import { admin_performance_rate_path, edit_admin_performance_rate_path } from "routes";
import { api, ApiError } from "core/api";

type PerformanceRateItemsFormProps = {
  performanceRateId: number;
};

const PerformanceRateItemsForm = ({ performanceRateId }: PerformanceRateItemsFormProps): JSX.Element => {
  const formMethods = useForm<PerformanceRateFormData>({
    mode: "onChange",
    resolver: yupResolver(PerformanceRateSchema)
  });
  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting, isValidating }
  } = formMethods;
  const fieldArrayName = "performance_rate_items_attributes";
  const { fields, append, insert, remove } = useFieldArray({
    control,
    name: fieldArrayName,
    keyName: "key"
  });
  const [formErrors, setFormErrors] = React.useState<Array<string>>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isCostSavings, setIsCostSavings] = React.useState(false);
  const materialVarianceRateLength = fields.filter((field) => field.rating_type === "total_material_variance_rate_25").length;

  const getPerformanceRate = async (id: number) => {
    try {
      setIsLoading(true);
      const response = await api.get(edit_admin_performance_rate_path(id));

      if (response.data.name === "Cost Savings (60%)") {
        setIsCostSavings(true);
      }
      reset(response.data);
    } catch (error) {
      if (error instanceof ApiError) {
        setFormErrors(error.all());
      } else {
        setFormErrors([error?.message]);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const save = async (data: PerformanceRateFormData) => {
    setFormErrors([]);
    try {
      if (data.id) {
        const response = await api.put(admin_performance_rate_path(data.id), data);
        getPerformanceRate(response.data.id);
      }
    } catch (error) {
      if (error instanceof ApiError) {
        setFormErrors(error.all());
      } else {
        setFormErrors([error?.message]);
      }
    }
  };

  const onError = (data) => {
    console.error(data);

    if (data?.performance_rate_items_attributes) {
      setFormErrors([data.performance_rate_items_attributes.message]);
    } else {
      setFormErrors(["There are errors on your form."]);
    }
  };

  const loader = () => {
    return (
      <div className="d-flex justify-content-center align-items-center h-100 pt-lg-8 pb-lg-8">
        <Spinner animation="border" role="status" variant="brand">
          <span className="visually-hidden">Loading Performance Rates...</span>
        </Spinner>
      </div>
    );
  };

  React.useEffect(() => {
    getPerformanceRate(performanceRateId);
  }, []);

  return (
    <React.StrictMode>
      <FormProvider {...formMethods}>
        <Form errors={formErrors}>
          <Card.Body>
            {isLoading ? (
              loader()
            ) : (
              <Container fluid={true}>
                <Row className="align-items-center">
                  <Col>
                    <h5>{I18n.t(`activerecord.attributes.performance_rate_item.rating_types.${fields?.[0]?.rating_type}`)}</h5>
                  </Col>
                  <Col sm="auto">
                    <Button
                      className="button--brand mr-3"
                      loading={isSubmitting || isValidating}
                      disabled={isSubmitting || isValidating}
                      icon="save"
                      title="Save"
                      onClick={() => {
                        handleSubmit(async (data) => await save(data), onError)();
                      }}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col>
                    {fields.map((item, index) => {
                      return (
                        <PerformanceRateItemForm
                          key={item.key}
                          id={item.id}
                          index={index}
                          remove={remove}
                          isCostSavings={isCostSavings}
                          materialVarianceRateLength={materialVarianceRateLength}
                        />
                      );
                    })}
                  </Col>
                </Row>
                <Row>
                  <Col sm="auto" className="d-flex justify-content-end">
                    <Button
                      className="button--brand"
                      title={isCostSavings ? "Add Material Variance Rate" : "Add"}
                      icon="plus"
                      type="button"
                      onClick={() => {
                        isCostSavings
                          ? insert(materialVarianceRateLength, {
                              rating_type: PerformanceRateItemRatingType.TotalMaterialVarianceRate25,
                              min: 0,
                              max: 0,
                              rating: 0
                            })
                          : append({ rating_type: fields?.[0]?.rating_type });
                      }}
                    />
                    {isCostSavings && (
                      <Button
                        className="button--brand ml-3"
                        title="Add Labor Variance Rate"
                        icon="plus"
                        type="button"
                        onClick={() => {
                          append({ rating_type: PerformanceRateItemRatingType.TotalLaborVarianceRate35 });
                        }}
                      />
                    )}
                  </Col>
                </Row>
              </Container>
            )}
          </Card.Body>
        </Form>
      </FormProvider>
    </React.StrictMode>
  );
};

export default PerformanceRateItemsForm;
